<template>
  <div>
    <page-header
      :actions="headerActions"
      left-side-class="col-xl-4 pb-2"
      :page="headerPage"
      right-side-class="col-xl-8 align-items-end d-flex pb-4 justify-content-end"
    />
    <board v-if="id" class="my-3" :details="board">
      <template #data-item="{ item }">
        <div v-if="item.status">
          <custom-balloon
            :background-color="item.status.color"
            dot
            :text="item.status.name"
          />
        </div>
      </template>
    </board>

    <q-tab
      :active-tab="activeTab.index"
      nav-class="text-nowrap"
      :tab-activated="beforeTabChange"
      @change="setTab"
    >
      <q-tab-item
        v-for="tab in tabs"
        :key="tab.id"
        :active="activeTab.id === tab.id"
        :disabled="
          activeTab.id !== tab.id &&
            (loading || isSaving || activeTab.options.disabled)
        "
        :has-error="!tab.valid"
        lazy
        :nav-item-class="navItemClass(tab)"
        :tabindex="tab.index"
        :title="tab.title"
      >
        <keep-alive>
          <component
            :is="tab.id"
            :id="id"
            :ref="tab.id"
            v-model="tab.model"
            v-bind="tab.options"
            :appointment-id="appointmentId"
            :has-occupational-pairing="model.hasOccupationalPairing"
            :is-in-private-school="isInPrivateSchool"
            :is-read-only="isTabReadOnly(tab)"
            :status-id="model.status?.id"
            :title="tab.title"
            :valid="tab.valid"
            @signature-status-changed="handleSignatureStatusChanged"
            @update:can-complete-signature="setCanCompleteSignature"
            @update:disabled="tab.options.disabled = $event"
            @update:has-occupational-pairing="setHasOccupationalPairing"
            @update:initialModel="setInitialModel(tab, $event)"
            @update:signature-hub-info="setSignatureHubInfo"
          />
        </keep-alive>
      </q-tab-item>
    </q-tab>

    <div v-if="canSave" class="d-flex justify-content-end">
      <action-button
        i="fas fa-save"
        text="Guardar"
        variant="success pl-3 pr-3 my-3"
        @click="save()"
      />
    </div>
  </div>
</template>

<script>
//mixins
import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';

//utils
import { copy, equals } from '@/utils/objUtils';
import { getSignatureHubInfo as getSignatureHubInfoApi } from '@/modules/signatureHub/api';

//filters
import { emptyFilter as empty } from '@/filters';

//components
import PageHeader from 'PageHeader';
import Board from 'Board';
import CustomBalloon from 'custom-balloon';
import { QTab, QTabItem } from '@/components/common/q-tabs';
import ActionButton from 'ActionButton';

//local-components

//tabs
import {
  Appointment,
  OnePartA,
  OnePartB,
  Two,
  ThreeToFour,
  Five,
  Six,
  Seven,
  Eight,
  Nine,
  Ten,
  Signature,
} from './tabs';
import TabModels from './tabs/models';

//services
import {
  get,
  save,
  changeStatus,
} from '@/modules/vocational-evaluation/api.js';
import _generalApi from '@/modules/app/api';

//constants
import Statuses, {
  canceledDocument as CanceledDocumentStatus,
  draft as DraftStatus,
  rejectedSignature as RejectedSignatureStatus,
} from '@/utils/constants/vocationalEducation';
import SignatureHubTypes from '@/utils/constants/signatureHubTypes';
import DigitalSignatureDocumentStatus from '@/utils/constants/digitalSignatureDocumentStatus';

export default {
  name: 'VocationalEducationMain',
  components: {
    PageHeader,
    Board,
    CustomBalloon,
    QTab,
    QTabItem,
    ActionButton,
    Appointment,
    OnePartA,
    OnePartB,
    Two,
    ThreeToFour,
    Five,
    Six,
    Seven,
    Eight,
    Nine,
    Ten,
    Signature,
  },
  mixins: [storeMixin, alertMixin],
  props: {
    appointmentId: {
      type: [Number, String],
      required: true,
    },
    id: {
      type: [Number, String],
      default: () => null,
    },
  },
  data() {
    return {
      loading: false,
      isSaving: false,
      tabChangeConfirmed: false,
      model: { status: DraftStatus, id: null },
      canCompleteSignatureDocument: false,
      signatureHubInfo: null,
      signatureStatusRefreshInProgress: false,
      tabToChange: null,
      tabs: [
        TabModels.Appointment,
        TabModels.OnePartA,
        TabModels.OnePartB,
        TabModels.Two,
        TabModels.ThreeToFour,
        TabModels.Five,
        TabModels.Six,
        TabModels.Seven,
        TabModels.Eight,
        TabModels.Nine,
        TabModels.Ten,
        TabModels.Signature,
      ]
        .filter(({ options }) => !options.hidden)
        .map((x, index) => ({ index, ...x })),
    };
  },
  computed: {
    headerPage() {
      return {
        title: 'Evaluación Vocacional',
        icon: 'fas fa-file-invoice',
        routes: ['Inicio', 'Proveedor', 'Evaluación Vocacional'],
      };
    },
    headerActions() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.back(),
        },
        {
          text: 'Guardar',
          icon: 'fas fa-save',
          variant: 'success',
          visible: this.canSave,
          click: () => this.save(),
        },
        {
          text: 'Someter',
          icon: 'fas fa-paper-plane',
          variant: 'success',
          visible: this.isDraft,
          click: () => this.save(true),
        },
        {
          text: 'Completar',
          icon: 'fas fa-flag',
          variant: 'success',
          visible: this.canCompleteSignature,
          click: () => this.completeSignature(),
        },
        {
          text: 'Devolver a Borrador',
          icon: 'fas fa-undo',
          variant: 'outline-success',
          visible: this.canReturnToDraft,
          click: () => this.returnToDraft(),
        },
        {
          text: 'Imprimir',
          icon: 'fas fa-print',
          variant: 'outline-success',
          visible: this.isCompleted,
          click: () => this.printReport(),
        },
      ];
    },
    board() {
      return [
        [
          {
            label: 'Número de Informe:',
            icon: 'fa fa-file-alt',
            cols: 'col-12 col-md',
            data: empty(this.model.number),
          },
          {
            label: 'Estatus:',
            icon: 'fas fa-file-invoice',
            cols: 'col-12 col-md',
            status: this.model.status,
          },
          {
            label: 'Número de Cita:',
            icon: 'fas fa-file-alt',
            cols: 'col-12 col-md',
            data: empty(this.model.appointmentId),
          },
          {
            label: 'Tipo de Evaluación:',
            icon: 'fas fa-clipboard-list',
            cols: 'col-12 col-md',
            data: empty(this.model.type?.name),
          },
          {
            label: 'Servicio:',
            icon: 'fas fa-clipboard-list',
            cols: 'col-12 col-md',
            data: empty(this.model.discipline?.name),
          },
        ],
      ];
    },
    isDraft() {
      return this.model.status?.id === Statuses.Draft;
    },
    isReadOnly() {
      return !this.isDraft && !this.isReturnedSignatureStatus;
    },
    isCompleted() {
      return this.model.status?.id === Statuses.Completed;
    },
    isRejectedSignature() {
      return this.model.status?.id === Statuses.RejectedSignature;
    },
    isCanceledDocument() {
      return this.model.status?.id === Statuses.CanceledDocument;
    },
    isReturnedSignatureStatus() {
      return this.isRejectedSignature || this.isCanceledDocument;
    },
    canReturnToDraft() {
      return (
        this.isSignatureStatus &&
        this.userHasPermissions(
          'provider.appointmentmanagement.vocationalassessment.returndraft'
        )
      );
    },
    isSignatureStatus() {
      return [
        this.model.status?.id === Statuses.PendingSignature,
        this.model.status?.id === Statuses.RejectedSignature,
        this.model.status?.id === Statuses.CanceledDocument,
      ].includes(true);
    },
    showSignatureTab() {
      return this.isSignatureStatus || this.isCompleted;
    },
    canSave() {
      return this.isDraft || this.isReturnedSignatureStatus;
    },
    canCompleteSignature() {
      return (
        this.model.status?.id === Statuses.PendingSignature &&
        this.canCompleteSignatureDocument
      );
    },
    hasClosedSignatureHubDocument() {
      return [
        DigitalSignatureDocumentStatus.Canceled,
        DigitalSignatureDocumentStatus.RenderedIneffective,
        DigitalSignatureDocumentStatus.Rejected,
      ].includes(Number(this.signatureHubInfo?.documentInfo?.documentStatusId));
    },
    isInPrivateSchool() {
      return this.model.isInPrivateSchool ?? false;
    },
    hasSaveOnActive() {
      const { preventBack, preventRouteLeave } = this.activeTab.options;
      return preventBack || preventRouteLeave;
    },
    hasChanges() {
      if (this.isTabReadOnly(this.activeTab)) return false;
      const { model, options } = this.activeTab;
      if (!model || !options.initialModel) return false;
      return this.hasSaveOnActive
        ? !equals(model, options.initialModel)
        : false;
    },
    preventTabChangeDisabled() {
      return [
        !this.activeTab.options.preventTabChange,
        !this.hasChanges,
        this.tabChangeConfirmed,
      ].includes(true);
    },
    activeTab: {
      get() {
        const tab = this.$route.query.tab;
        const index = tab ? this.tabs.findIndex((_, i) => i == tab) : 0;
        return this.tabs[index];
      },
      set({ index: tab }) {
        const current = this.$route.query.tab ?? null;
        const next = tab ? String(tab) : null;
        if (current === next) return;

        if (next) this.$router.replace({ query: { tab: next } });
        else {
          const { name, params } = this.$route;
          this.$router.replace({ name, params });
        }
      },
    },
    signatureTab() {
      return this.tabs.find((tab) => tab.id === TabModels.Signature.id);
    },
  },
  async mounted() {
    await this.get(this.id);
  },
  methods: {
    setInitialModel(tab, value) {
      this.$nextTick(() => (tab.options.initialModel = copy(value)));
    },
    resetActiveTabInitialModel() {
      const tab = this.activeTab;
      if (!tab?.model) return;

      this.setInitialModel(tab, tab.model);
    },
    setCanCompleteSignature(value) {
      this.canCompleteSignatureDocument = Boolean(value);
    },
    async setSignatureHubInfo(value) {
      this.signatureHubInfo = value;
      await this.syncEvaluationStatusFromSignatureHub(value);
    },
    setHasOccupationalPairing(value) {
      if (value === undefined) return;
      this.model = {
        ...this.model,
        hasOccupationalPairing: value,
      };
    },
    navItemClass(tab) {
      const hidden = [
        !this.model?.hasOccupationalPairing &&
          [TabModels.Seven.id, TabModels.Eight.id].includes(tab.id),
        tab.id === TabModels.Signature.id && !this.showSignatureTab,
      ].includes(true);
      return hidden ? 'd-none' : '';
    },
    isTabReadOnly(tab) {
      if (tab?.id === TabModels.Signature.id) return this.isCompleted;
      if (this.model.status?.id === Statuses.PendingSignature) return true;
      return this.isReadOnly;
    },
    setTab(tab) {
      if (this.loading || this.isSaving) return;
      this.activeTab = this.tabs[tab];
    },
    async beforeTabChange(tab, _, e) {
      if (this.preventTabChangeDisabled || this.tabToChange === tab) {
        this.tabToChange = null;
        return;
      }
      if ([null, undefined].includes(this.tabToChange)) {
        e.preventDefault();
      }
      if (this.loading || this.isSaving) {
        return;
      }
      this.tabToChange = tab;
      const { cancelled } = await this.confirm();
      if (cancelled) {
        this.tabToChange = null;
        return;
      }
      this.tabChangeConfirmed = true;
      this.setTab(tab);
      this.tabChangeConfirmed = false;
    },
    async back() {
      if (this.activeTab.options.preventBack) {
        const { cancelled } = await this.confirm();
        if (cancelled) return;
      }
      this.$router.back();
    },
    async get(id) {
      this.loading = true;
      const { data } = await get({
        id,
        appointmentId: this.appointmentId,
      });
      if (!data) {
        this.loading = false;
        return;
      }
      this.model = data;
      await this.getSignatureHubInfo();
      this.tabChangeConfirmed = false;
      if (this.navItemClass(this.activeTab) === 'd-none') {
        this.setTab(0);
      }
      if (this.$route.params.id == data.id) {
        this.loading = false;
        return;
      }
      this.loading = false;
      this.$router.push({
        name: this.$route.name,
        params: { ...this.$route.params, id: data.id },
        query: { ...this.$route.query },
      });
    },
    async getSignatureHubInfo() {
      if (!this.showSignatureTab) {
        this.signatureHubInfo = null;
        return;
      }

      try {
        const { data } = await getSignatureHubInfoApi(
          SignatureHubTypes.VOCATIONAL_EVALUATION,
          Number(this.id)
        );
        this.signatureHubInfo = data;
        await this.syncEvaluationStatusFromSignatureHub(data);
      } catch {
        this.signatureHubInfo = null;
      }
    },
    getEvaluationStatusFromSignatureHubInfo(value) {
      const documentStatusId = Number(value?.documentInfo?.documentStatusId);
      const statusByDocumentStatus = {
        [DigitalSignatureDocumentStatus.Canceled]: CanceledDocumentStatus,
        [DigitalSignatureDocumentStatus.RenderedIneffective]: CanceledDocumentStatus,
        [DigitalSignatureDocumentStatus.Rejected]: RejectedSignatureStatus,
      };

      return statusByDocumentStatus[documentStatusId] ?? null;
    },
    async syncEvaluationStatusFromSignatureHub(value) {
      const status = this.getEvaluationStatusFromSignatureHubInfo(value);
      if (!status || this.model.status?.id !== Statuses.PendingSignature)
        return;

      try {
        await changeStatus(this.id, status.id);
        this.model = {
          ...this.model,
          status,
        };
      } catch {
        this.ShowToast(
          '',
          'No fue posible sincronizar el estatus de firma de la evaluación.',
          'error'
        );
      }
    },
    async confirm() {
      if (!this.hasChanges) return { cancelled: false, confirmed: false };
      const { value: confirmed, dismiss } = await this.ShowCancelModalClosable(
        'Confirmación',
        '<div>Cuenta con cambios sin guardar,</div><div>¿Desea guardar la información previamente documentada?</div>'
      );
      const cancelled = ['close', 'backdrop'].includes(dismiss);
      if (cancelled) return { cancelled, confirmed: false };
      if (confirmed) await this.save();
      return { confirmed, cancelled };
    },
    async completeSignature() {
      if (!this.canCompleteSignatureDocument) {
        if (this.signatureTab) this.setTab(this.signatureTab.index);
        this.ShowToast(
          '',
          'Debe adjuntar el documento firmado para completar.',
          'warning'
        );
        return;
      }
      if (this.activeTab.id !== TabModels.Signature.id) {
        if (this.signatureTab) this.setTab(this.signatureTab.index);
        this.ShowToast(
          '',
          'Debe completar la información del Tab Firma.',
          'warning'
        );
        return;
      }
      await this.save(true);
    },
    async returnToDraft() {
      try {
        await changeStatus(this.id, Statuses.Draft);
        this.setTab(0);
        await this.get(this.id);
        this.ShowToast(
          '',
          'La Evaluación Vocacional regresó a Borrador.',
          'success'
        );
      } catch {
        this.ShowToast(
          '',
          'No fue posible cambiar el estatus. Intente nuevamente o contacte al administrador.',
          'error'
        );
      }
    },
    async handleSignatureStatusChanged() {
      await this.get(this.id);
    },
    async save(isSubmitted = false) {
      if (this.isSaving) return;
      this.isSaving = true;
      try {
        const { id, model, build, confirm } = this.activeTab;
        if (isSubmitted) {
          this.clearErrorTabs();
          await this.$refs[id][0]?.validate();
        }
        const tabModel = copy(model);
        if (confirm) {
          const confirmed = await confirm(tabModel, copy(this.model));
          if (!confirmed) return;
        }
        const payload = {
          ...(await build(tabModel)),
          id: this.id && this.id > 0 ? Number(this.id) : null,
          appointmentId: Number(this.appointmentId),
          statusId: Number(this.model.status?.id ?? Statuses.Draft),
          isSubmitted,
        };

        const { data } = await save(payload);

        if (data.invalid) {
          this.setInvalidTabs(data.invalidTabs.map((x) => x.tab));
          this.setInitialModel(this.activeTab, this.activeTab.model);
          return;
        }
        this.setInitialModel(this.activeTab, this.activeTab.model);

        await this.get(data.id);
        this.resetActiveTabInitialModel();

        if (isSubmitted) {
          const message = this.isCompleted
            ? 'La Evaluación Vocacional fue completada exitosamente'
            : 'La Evaluación Vocacional fue sometida exitosamente';
          this.ShowToast('', message, 'success');
          if (this.isSignatureStatus && this.signatureTab) {
            this.setTab(this.signatureTab.index);
          }
        }
      } catch {
        this.ShowToast(
          '',
          'No fue posible guardar la información. Intente nuevamente o contacte al administrador.',
          'error'
        );
      } finally {
        this.isSaving = false;
      }
    },
    async setInvalidTabs(ids = []) {
      if (ids.length == 0) return;
      const tabs = this.tabs.filter((x) => ids.includes(x.id));
      tabs.forEach((x) => (x.valid = false));
      const titles = tabs.map((x) => `<b>${x.title}</b>`).join(', ');
      const prefix = tabs.length === 1 ? `El tab` : `Los tabs`;
      const sufix = tabs.length === 1 ? `tiene` : `tienen`;
      this.ShowToast(
        '',
        `No fue posible someter la evaluación vocacional. ${prefix} ${titles} ${sufix} datos inválidos.`,
        'error'
      );
    },
    async printReport() {
      await _generalApi.downloadDocumentByAttachmentId(
        this.model.appointmentDocumentId
      );
    },
    clearErrorTabs(ids = []) {
      const tabs = ids.length
        ? this.tabs.filter((x) => ids.includes(x.id))
        : this.tabs;

      tabs.forEach((x) => (x.valid = true));
    },
  },
  async beforeRouteLeave(_, __, next) {
    try {
      if (this.activeTab.options.preventRouteLeave) {
        const { cancelled } = await this.confirm();
        if (cancelled) return false;
      }
      next();
    } catch {
      return false;
    }
  },
};
</script>
<style scoped>
::v-deep .orange-icon {
  font-size: 18pt;
}
</style>
